import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Calculator,
  Search,
  MapPin,
  Phone,
  Mail,
  Star,
  Globe,
  Shield,
  Scale,
  BookOpen,
  ChevronDown,
  ArrowLeft,
  Users,
  X,
  Crown,
  MessageSquare,
  Send,
  Loader2,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  enrolled_agent: {
    label: "Enrolled Agent",
    shortLabel: "EA",
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: Shield,
    badgeColor: "bg-emerald-600",
  },
  cpa: {
    label: "CPA",
    shortLabel: "CPA",
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: BookOpen,
    badgeColor: "bg-blue-600",
  },
  tax_attorney: {
    label: "Tax Attorney",
    shortLabel: "JD",
    color: "bg-purple-100 text-purple-800 border-purple-200",
    icon: Scale,
    badgeColor: "bg-purple-600",
  },
};

function StarRating({ rating, count }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i <= Math.round(rating)
                ? "fill-amber-400 text-amber-400"
                : "text-slate-200"
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-semibold text-slate-700">{rating}</span>
      <span className="text-sm text-slate-400">({count})</span>
    </div>
  );
}

function ProfessionalCard({ professional, onViewReviews, onContact }) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[professional.type] || TYPE_CONFIG.enrolled_agent;
  const IconComponent = config.icon;

  const initials = professional.name
    .split(" ")
    .filter((p) => !p.includes(",") && !p.includes("."))
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  return (
    <Card
      className="group hover:shadow-lg transition-all duration-300 border border-slate-200 hover:border-slate-300 overflow-hidden"
      data-testid={`pro-card-${professional.id}`}
    >
      <CardContent className="p-0">
        {/* Top bar accent */}
        <div className={`h-1.5 ${config.badgeColor}`} />

        {professional.is_featured && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Featured</span>
          </div>
        )}

        <div className="p-5">
          {/* Header: Avatar + Name + Type */}
          <div className="flex gap-4 mb-4">
            <div
              className={`w-14 h-14 rounded-xl ${config.badgeColor} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-slate-800 text-lg leading-tight truncate">
                {professional.name}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${config.color}`}
                >
                  <IconComponent className="w-3 h-3" />
                  {config.label}
                </span>
                <span className="text-xs text-slate-400">
                  {professional.years_experience}yr exp
                </span>
              </div>
            </div>
          </div>

          {/* Rating */}
          <StarRating
            rating={professional.rating}
            count={professional.review_count}
          />

          {/* Location */}
          <div className="flex items-center gap-1.5 mt-3 text-sm text-slate-600">
            <MapPin className="w-4 h-4 text-slate-400" />
            {professional.city}, {professional.state}
          </div>

          {/* Specialties */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {professional.specialties.slice(0, 3).map((s, i) => (
              <span
                key={i}
                className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md"
              >
                {s}
              </span>
            ))}
            {professional.specialties.length > 3 && (
              <span className="text-xs text-slate-400 px-1 py-1">
                +{professional.specialties.length - 3} more
              </span>
            )}
          </div>

          {/* Bio (expandable) */}
          <div className="mt-3">
            <p
              className={`text-sm text-slate-500 leading-relaxed ${
                expanded ? "" : "line-clamp-2"
              }`}
            >
              {professional.bio}
            </p>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-blue-600 hover:text-blue-700 mt-1 font-medium"
              data-testid={`toggle-bio-${professional.id}`}
            >
              {expanded ? "Show less" : "Read more"}
            </button>
          </div>

          {/* Contact buttons */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
            <a
              href={`tel:${professional.phone}`}
              className="flex-1"
              data-testid={`call-btn-${professional.id}`}
            >
              <Button
                variant="outline"
                className="w-full text-sm h-9 border-slate-200"
              >
                <Phone className="w-3.5 h-3.5 mr-1.5" />
                Call
              </Button>
            </a>
            <a
              href={`mailto:${professional.email}`}
              className="flex-1"
              data-testid={`email-btn-${professional.id}`}
            >
              <Button
                variant="outline"
                className="w-full text-sm h-9 border-slate-200"
              >
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                Email
              </Button>
            </a>
            {professional.website && (
              <a
                href={professional.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
                data-testid={`web-btn-${professional.id}`}
              >
                <Button
                  variant="outline"
                  className="text-sm h-9 px-3 border-slate-200"
                >
                  <Globe className="w-3.5 h-3.5" />
                </Button>
              </a>
            )}
          </div>

          {/* Action buttons row */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => onViewReviews(professional)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors font-medium"
              data-testid={`reviews-btn-${professional.id}`}
            >
              <MessageSquare className="w-4 h-4" />
              {professional.review_count > 0
                ? `${professional.review_count} Review${professional.review_count !== 1 ? "s" : ""}`
                : "Reviews"}
            </button>
            <button
              onClick={() => onContact(professional)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors font-medium border-l border-slate-100"
              data-testid={`contact-btn-${professional.id}`}
            >
              <Send className="w-4 h-4" />
              Contact
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TaxProDirectory() {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedType, setSelectedType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [typeOpen, setTypeOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [specialtyOpen, setSpecialtyOpen] = useState(false);

  // Review modal state
  const [reviewModal, setReviewModal] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewMessage, setReviewMessage] = useState("");

  // Contact modal state
  const [contactModal, setContactModal] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", tax_needs: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactMessage, setContactMessage] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchFilters();
    fetchProfessionals();
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [selectedType, selectedState, selectedSpecialty, searchQuery]);

  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${API}/directory/filters`);
      setFilters(res.data);
    } catch (err) {
      console.error("Failed to load filters:", err);
    }
  };

  const fetchProfessionals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedType) params.set("type", selectedType);
      if (selectedState) params.set("state", selectedState);
      if (selectedSpecialty) params.set("specialty", selectedSpecialty);
      if (searchQuery) params.set("search", searchQuery);
      const res = await axios.get(
        `${API}/directory/professionals?${params.toString()}`
      );
      setProfessionals(res.data);
    } catch (err) {
      console.error("Failed to load professionals:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedType("");
    setSelectedState("");
    setSelectedSpecialty("");
    setSearchQuery("");
  };

  const openReviewModal = async (professional) => {
    setReviewModal(professional);
    setReviewForm({ rating: 5, title: "", comment: "" });
    setReviewMessage("");
    setReviewsLoading(true);
    try {
      const res = await axios.get(`${API}/directory/professionals/${professional.id}/reviews`);
      setReviews(res.data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const submitReview = async () => {
    if (!token) { setReviewMessage("Please sign in to leave a review."); return; }
    if (!reviewForm.title || !reviewForm.comment) { setReviewMessage("Please fill in all fields."); return; }
    setReviewSubmitting(true);
    setReviewMessage("");
    try {
      await axios.post(
        `${API}/directory/professionals/${reviewModal.id}/reviews`,
        reviewForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh reviews and professionals list
      const [revRes] = await Promise.all([
        axios.get(`${API}/directory/professionals/${reviewModal.id}/reviews`),
        fetchProfessionals(),
      ]);
      setReviews(revRes.data);
      setReviewForm({ rating: 5, title: "", comment: "" });
      setReviewMessage("Review submitted successfully!");
      // Update the modal professional's rating
      const updatedPro = await axios.get(`${API}/directory/professionals/${reviewModal.id}`);
      setReviewModal(updatedPro.data);
    } catch (err) {
      setReviewMessage(err.response?.data?.detail || "Failed to submit review.");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const hasActiveFilters =
    selectedType || selectedState || selectedSpecialty || searchQuery;

  const openContactModal = (professional) => {
    setContactModal(professional);
    setContactForm({ name: "", email: "", phone: "", tax_needs: "" });
    setContactMessage("");
  };

  const submitInquiry = async () => {
    if (!token) { setContactMessage("Please sign in to send a contact request."); return; }
    if (!contactForm.name || !contactForm.email || !contactForm.tax_needs) {
      setContactMessage("Please fill in your name, email, and tax needs.");
      return;
    }
    setContactSubmitting(true);
    setContactMessage("");
    try {
      await axios.post(
        `${API}/directory/professionals/${contactModal.id}/inquiries`,
        contactForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContactMessage("success");
    } catch (err) {
      setContactMessage(err.response?.data?.detail || "Failed to send inquiry.");
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span
                className="text-2xl font-bold text-slate-800"
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                Tax Helper Pros
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Home
              </Button>
              <Button
                onClick={() => navigate("/auth")}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-12 text-center">
          <h1
            className="text-4xl sm:text-5xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
            data-testid="directory-title"
          >
            Find a Tax Pro
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
            Connect with licensed Enrolled Agents, CPAs, and Tax Attorneys ready
            to help with your tax needs.
          </p>

          {/* Search bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, city, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base rounded-xl border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-200"
              data-testid="directory-search"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Type filter */}
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => {
                setTypeOpen(!typeOpen);
                setStateOpen(false);
                setSpecialtyOpen(false);
              }}
              className={`h-10 ${
                selectedType
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-slate-200"
              }`}
              data-testid="filter-type"
            >
              {selectedType
                ? TYPE_CONFIG[selectedType]?.label || selectedType
                : "Professional Type"}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            {typeOpen && (
              <div className="absolute top-12 left-0 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[220px]">
                <button
                  onClick={() => {
                    setSelectedType("");
                    setTypeOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 text-slate-500"
                >
                  All Types
                </button>
                {filters?.types?.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => {
                      setSelectedType(t.value);
                      setTypeOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 ${
                      selectedType === t.value
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* State filter */}
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => {
                setStateOpen(!stateOpen);
                setTypeOpen(false);
                setSpecialtyOpen(false);
              }}
              className={`h-10 ${
                selectedState
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-slate-200"
              }`}
              data-testid="filter-state"
            >
              {selectedState || "State"}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            {stateOpen && (
              <div className="absolute top-12 left-0 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[140px] max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedState("");
                    setStateOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 text-slate-500"
                >
                  All States
                </button>
                {filters?.states?.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSelectedState(s);
                      setStateOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 ${
                      selectedState === s
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-700"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Specialty filter */}
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => {
                setSpecialtyOpen(!specialtyOpen);
                setTypeOpen(false);
                setStateOpen(false);
              }}
              className={`h-10 ${
                selectedSpecialty
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-slate-200"
              }`}
              data-testid="filter-specialty"
            >
              {selectedSpecialty || "Specialty"}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            {specialtyOpen && (
              <div className="absolute top-12 left-0 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[240px] max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedSpecialty("");
                    setSpecialtyOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 text-slate-500"
                >
                  All Specialties
                </button>
                {filters?.specialties?.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSelectedSpecialty(s);
                      setSpecialtyOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 ${
                      selectedSpecialty === s
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-700"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="h-10 text-slate-500 hover:text-red-600"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}

          {/* Result count */}
          <span className="text-sm text-slate-400 ml-auto">
            {professionals.length} professional
            {professionals.length !== 1 ? "s" : ""} found
          </span>
        </div>

        {/* Close dropdowns on outside click */}
        {(typeOpen || stateOpen || specialtyOpen) && (
          <div
            className="fixed inset-0 z-20"
            onClick={() => {
              setTypeOpen(false);
              setStateOpen(false);
              setSpecialtyOpen(false);
            }}
          />
        )}

        {/* Results grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : professionals.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              No professionals found
            </h3>
            <p className="text-slate-400 mb-4">
              Try adjusting your filters or search terms.
            </p>
            <Button variant="outline" onClick={clearFilters}>
              Clear all filters
            </Button>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="professionals-grid"
          >
            {professionals.map((pro) => (
              <ProfessionalCard key={pro.id} professional={pro} onViewReviews={openReviewModal} onContact={openContactModal} />
            ))}
          </div>
        )}

        {/* CTA: List Your Practice */}
        <div className="mt-16 mb-8">
          <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50">
            <CardContent className="py-10 text-center">
              <h2
                className="text-2xl font-bold text-slate-800 mb-3"
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                Are you a Tax Professional?
              </h2>
              <p className="text-slate-500 max-w-lg mx-auto mb-6">
                Get your practice listed on Tax Helper Pros and reach thousands
                of potential clients looking for expert tax help.
              </p>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 text-base"
                onClick={() => navigate("/tax-pro-portal")}
                data-testid="list-practice-btn"
              >
                List Your Practice
              </Button>
              <p className="text-xs text-slate-400 mt-3">
                Plans starting at $2.99/month
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setReviewModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{reviewModal.name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className={`w-4 h-4 ${i <= Math.round(reviewModal.rating) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-slate-600">{reviewModal.rating || "New"}</span>
                  <span className="text-sm text-slate-400">({reviewModal.review_count} reviews)</span>
                </div>
              </div>
              <button onClick={() => setReviewModal(null)} className="p-2 rounded-lg hover:bg-slate-200 transition-colors" data-testid="close-review-modal">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Write a review */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  Write a Review
                </h3>

                {!token ? (
                  <div className="text-center py-3">
                    <p className="text-sm text-slate-500 mb-2">Sign in to leave a review</p>
                    <Button variant="outline" size="sm" onClick={() => navigate("/auth")}>Sign In</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Star rating selector */}
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Rating</label>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map((i) => (
                          <button
                            key={i}
                            onClick={() => setReviewForm(p => ({...p, rating: i}))}
                            className="p-0.5 transition-transform hover:scale-110"
                            data-testid={`star-${i}`}
                          >
                            <Star className={`w-7 h-7 ${i <= reviewForm.rating ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Title</label>
                      <Input
                        value={reviewForm.title}
                        onChange={(e) => setReviewForm(p => ({...p, title: e.target.value}))}
                        placeholder="Summarize your experience"
                        className="bg-white"
                        data-testid="review-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Your Review</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm(p => ({...p, comment: e.target.value}))}
                        placeholder="Share your experience working with this professional..."
                        rows={3}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none bg-white"
                        data-testid="review-comment"
                      />
                    </div>
                    {reviewMessage && (
                      <div className={`text-sm px-3 py-2 rounded-lg ${
                        reviewMessage.includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>{reviewMessage}</div>
                    )}
                    <Button
                      onClick={submitReview}
                      disabled={reviewSubmitting}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="submit-review-btn"
                    >
                      {reviewSubmitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Submit Review</>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Reviews list */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">
                  Reviews ({reviews.length})
                </h3>
                {reviewsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : reviews.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">
                    No reviews yet. Be the first to review this professional!
                  </p>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`review-${review.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold text-slate-700">{review.username}</span>
                              <div className="flex gap-0.5">
                                {[1,2,3,4,5].map((i) => (
                                  <Star key={i} className={`w-3.5 h-3.5 ${i <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                                ))}
                              </div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(review.created_date).toLocaleDateString()}
                          </span>
                        </div>
                        <h4 className="font-medium text-slate-800 text-sm mb-1">{review.title}</h4>
                        <p className="text-sm text-slate-500 leading-relaxed">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Inquiry Modal */}
      {contactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="contact-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setContactModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-emerald-50">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Contact {contactModal.name}</h2>
                <p className="text-sm text-slate-500 mt-0.5">Send an inquiry about your tax needs</p>
              </div>
              <button onClick={() => setContactModal(null)} className="p-2 rounded-lg hover:bg-emerald-100 transition-colors" data-testid="close-contact-modal">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-6">
              {contactMessage === "success" ? (
                <div className="text-center py-6" data-testid="contact-success">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">Inquiry Sent!</h3>
                  <p className="text-sm text-slate-500 mb-6">
                    Your message has been sent to {contactModal.name}. They'll be able to see your inquiry and reach out to you directly.
                  </p>
                  <Button onClick={() => setContactModal(null)} variant="outline" data-testid="close-contact-success">
                    Close
                  </Button>
                </div>
              ) : !token ? (
                <div className="text-center py-6">
                  <p className="text-sm text-slate-500 mb-4">Sign in to send a contact request</p>
                  <Button onClick={() => navigate("/auth")} className="bg-blue-600 hover:bg-blue-700 text-white">
                    Sign In / Register
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Your Name *</label>
                      <Input
                        value={contactForm.name}
                        onChange={(e) => setContactForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="John Doe"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Your Email *</label>
                      <Input
                        value={contactForm.email}
                        onChange={(e) => setContactForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="you@example.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone (optional)</label>
                    <Input
                      value={contactForm.phone}
                      onChange={(e) => setContactForm(p => ({ ...p, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">What tax help do you need? *</label>
                    <textarea
                      value={contactForm.tax_needs}
                      onChange={(e) => setContactForm(p => ({ ...p, tax_needs: e.target.value }))}
                      placeholder="Describe your tax situation or what services you're looking for..."
                      rows={4}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                      data-testid="contact-tax-needs"
                    />
                  </div>
                  {contactMessage && contactMessage !== "success" && (
                    <div className="text-sm px-3 py-2 rounded-lg bg-red-100 text-red-700" data-testid="contact-error">
                      {contactMessage}
                    </div>
                  )}
                  <Button
                    onClick={submitInquiry}
                    disabled={contactSubmitting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11"
                    data-testid="submit-inquiry-btn"
                  >
                    {contactSubmitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Inquiry</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
